import { type FormEvent, useEffect, useState } from "react";
import { ApiError, type Company as CompanyDetails, api } from "./api";
import { PageHeader } from "./layout/Layout";
import { Alert, Box, Field, Loading, Working } from "./ui";
import { AiKey } from "./AiKey";

/**
 * The company's own details (quotation A10, A11).
 *
 * This is the letterhead: what a customer reads at the top of an invoice, and what the
 * bank reads on a payment voucher. It is typed in here once rather than sent over in a
 * message and pasted into the code, so it is OHC's to change and the change is kept with
 * everything else in the log.
 *
 * The heading on the right is drawn from the same values the printed document uses, so
 * what is on the screen is what will come out of the printer.
 */
export function Company({ canManage }: { readonly canManage: boolean }): React.ReactElement {
  const [company, setCompany] = useState<CompanyDetails | undefined>();
  const [form, setForm] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [saved, setSaved] = useState(false);
  /** Bumped after a new logo goes up, so the browser fetches the picture again. */
  const [logoAt, setLogoAt] = useState(0);

  function fill(details: CompanyDetails): void {
    setCompany(details);
    setForm({
      registeredName: details.registeredName ?? "",
      tradingName: details.tradingName ?? "",
      uen: details.uen ?? "",
      address: details.address ?? "",
      phone: details.phone ?? "",
      email: details.email ?? "",
      website: details.website ?? "",
      paymentDetails: details.paymentDetails ?? "",
      documentNote: details.documentNote ?? "",
    });
  }

  useEffect(() => {
    let live = true;
    api
      .company()
      .then((result) => {
        if (live) fill(result.company);
      })
      .catch((problem: unknown) => {
        if (live) setError(problem instanceof ApiError ? problem.message : "Could not load the company profile");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  const set = (name: string) => (changed: { target: { value: string } }) => {
    setForm((was) => ({ ...was, [name]: changed.target.value }));
    setSaved(false);
  };

  async function save(submitted: FormEvent): Promise<void> {
    submitted.preventDefault();
    setSaving(true);
    setError(undefined);
    try {
      const result = await api.saveCompany({
        registeredName: form.registeredName ?? null,
        tradingName: form.tradingName ?? null,
        uen: form.uen ?? null,
        address: form.address ?? null,
        phone: form.phone ?? null,
        email: form.email ?? null,
        website: form.website ?? null,
        paymentDetails: form.paymentDetails ?? null,
        documentNote: form.documentNote ?? null,
      });
      fill(result.company);
      setSaved(true);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not save the company profile");
    } finally {
      setSaving(false);
    }
  }

  async function putLogo(file: File): Promise<void> {
    setError(undefined);
    try {
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = "";
      for (let at = 0; at < bytes.length; at += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
      }
      const result = await api.saveLogo(file.type || "image/png", btoa(binary));
      setCompany(result.company);
      setLogoAt(Date.now());
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not upload the logo");
    }
  }

  /** Let the people who sell put their own sales right at the booth, or stop them. */
  async function putRight(on: boolean): Promise<void> {
    setError(undefined);
    try {
      setCompany((await api.setStaffPutRight(on)).company);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not change the setting");
    }
  }

  async function dropLogo(): Promise<void> {
    try {
      const result = await api.removeLogo();
      setCompany(result.company);
      setLogoAt(Date.now());
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not remove the logo");
    }
  }

  if (loading) return <Loading />;
  if (!company) return <Alert tone="danger">{error ?? "Could not load the company profile"}</Alert>;

  return (
    <>
      <PageHeader
        title="Company profile"
        subtitle="Company details and branding used on invoices and payment vouchers."
      />

      {error ? <Alert tone="danger">{error}</Alert> : null}
      {company.missing.length > 0 ? (
        <Alert tone="warning">
          Complete the company profile before issuing branded documents: {company.missing.join(", ")}.
        </Alert>
      ) : (
        <Alert tone="success">The company profile is complete.</Alert>
      )}

      <div className="grid grid-cols-12 gap-x-6">
        <div className="xxl:col-span-8 xl:col-span-7 col-span-12">
          <Box
            title="Company information"
            actions={
              company.updatedAt ? (
                <span className="text-[0.75rem] text-[#8c9097] dark:text-white/50">
                  {`Last updated by ${company.updatedByName ?? "a user"} on ${new Date(company.updatedAt).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" })}`}
                </span>
              ) : undefined
            }
          >
            <form onSubmit={(submitted) => void save(submitted)}>
              <div className="grid grid-cols-12 gap-4">
                <Field
                  id="company-registered-name"
                  label="Registered company name"
                  hint="Exactly as registered with ACRA, including Pte Ltd"
                  className="xl:col-span-8 col-span-12"
                >
                  <input
                    id="company-registered-name"
                    type="text"
                    className="form-control"
                    value={form.registeredName ?? ""}
                    onChange={set("registeredName")}
                    disabled={!canManage}
                  />
                </Field>
                <Field id="company-uen" label="Unique Entity Number (UEN)" className="xl:col-span-4 col-span-12">
                  <input
                    id="company-uen"
                    type="text"
                    className="form-control"
                    value={form.uen ?? ""}
                    onChange={set("uen")}
                    disabled={!canManage}
                  />
                </Field>
                <Field
                  id="company-trading-name"
                  label="Trading name"
                  hint="Include the trading name if customers know the business by another name"
                  className="col-span-12"
                >
                  <input
                    id="company-trading-name"
                    type="text"
                    className="form-control"
                    value={form.tradingName ?? ""}
                    onChange={set("tradingName")}
                    disabled={!canManage}
                  />
                </Field>
                <Field id="company-address" label="Address" className="col-span-12">
                  <textarea
                    id="company-address"
                    className="form-control"
                    rows={3}
                    value={form.address ?? ""}
                    onChange={set("address")}
                    disabled={!canManage}
                  />
                </Field>
                <Field id="company-phone" label="Phone number" className="xl:col-span-6 col-span-12">
                  <input
                    id="company-phone"
                    type="text"
                    className="form-control"
                    value={form.phone ?? ""}
                    onChange={set("phone")}
                    disabled={!canManage}
                  />
                </Field>
                <Field id="company-email" label="Email address" className="xl:col-span-6 col-span-12">
                  <input
                    id="company-email"
                    type="text"
                    className="form-control"
                    value={form.email ?? ""}
                    onChange={set("email")}
                    disabled={!canManage}
                  />
                </Field>
                <Field id="company-website" label="Website" className="col-span-12">
                  <input
                    id="company-website"
                    type="text"
                    className="form-control"
                    value={form.website ?? ""}
                    onChange={set("website")}
                    disabled={!canManage}
                  />
                </Field>
                <Field
                  id="company-payment-details"
                  label="Payment instructions"
                  hint="PayNow or bank details printed at the bottom of invoices"
                  className="col-span-12"
                >
                  <textarea
                    id="company-payment-details"
                    className="form-control"
                    rows={2}
                    value={form.paymentDetails ?? ""}
                    onChange={set("paymentDetails")}
                    disabled={!canManage}
                  />
                </Field>
                <Field
                  id="company-document-note"
                  label="Document footer text"
                  hint="Terms, a thank-you note or anything that should appear on every document. Leave empty if not needed"
                  className="col-span-12"
                >
                  <textarea
                    id="company-document-note"
                    className="form-control"
                    rows={2}
                    value={form.documentNote ?? ""}
                    onChange={set("documentNote")}
                    disabled={!canManage}
                  />
                </Field>
              </div>
              {canManage ? (
                <div className="flex items-center justify-end gap-3 mt-4">
                  {saved ? <span className="text-success text-[0.813rem]">Saved.</span> : null}
                  <button type="submit" className="ti-btn ti-btn-primary !mb-0" disabled={saving}>
                    {saving ? <Working label="Saving" /> : "Save changes"}
                  </button>
                </div>
              ) : (
                <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mt-4 mb-0">
                  Only a system administrator can change these details.
                </p>
              )}
            </form>
          </Box>
        </div>

        <div className="xxl:col-span-4 xl:col-span-5 col-span-12">
          <Box title="iPad sales settings">
            <label htmlFor="staff-put-right" className="flex items-start gap-2 text-[0.813rem] mb-0">
              <input
                id="staff-put-right"
                type="checkbox"
                className="form-check-input mt-0.5 shrink-0"
                checked={company.staffPutRight}
                disabled={!canManage}
                onChange={(changed) => void putRight(changed.target.checked)}
              />
              <span>
                <span className="font-semibold">Salespeople can void and correct sales without approval</span>
                <span className="block text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1">
                  No supervisor approval needed, for when everyone sells from one login. Every void and correction
                  still records who made it and why, on the management dashboard under Sales and buybacks. The iPads
                  apply the change within 5 minutes, or when the selling screen is reopened.
                </span>
              </span>
            </label>
          </Box>

          {canManage ? <AiKey /> : null}

          <Box title="Company logo">
            <div className="flex items-center justify-center bg-light dark:bg-bodybg rounded-sm p-4 mb-4 min-h-[7rem]">
              {company.hasLogo ? (
                <img
                  src={`/api/company/logo?at=${logoAt}`}
                  alt="The company logo"
                  className="max-h-[6rem] max-w-full object-contain"
                />
              ) : (
                <span className="text-[#8c9097] dark:text-white/50 text-[0.813rem]">No logo uploaded</span>
              )}
            </div>
            {canManage ? (
              <>
                <label htmlFor="company-logo" className="ti-btn ti-btn-light !mb-0 cursor-pointer">
                  <i className="ri-upload-2-line" /> Upload logo
                  <input
                    id="company-logo"
                    type="file"
                    accept="image/png,image/jpeg"
                    className="sr-only"
                    onChange={(changed) => {
                      const file = changed.target.files?.[0];
                      if (file) void putLogo(file);
                    }}
                  />
                </label>
                <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-2 mb-0">
                  A PNG with a transparent background prints best. Maximum 2 MB.
                </p>
                {company.hasLogo ? (
                  <button type="button" className="ti-btn ti-btn-light !mb-0 mt-3" onClick={() => void dropLogo()}>
                    Remove logo
                  </button>
                ) : null}
              </>
            ) : null}
          </Box>

          <Box title="Document preview">
            <div className="border border-dashed border-defaultborder dark:border-defaultborder/10 rounded-sm p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-[0.938rem] mb-1">
                    {form.registeredName || "Registered company name"}
                  </p>
                  {form.tradingName ? (
                    <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-1">
                      trading as {form.tradingName}
                    </p>
                  ) : null}
                  <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-1 whitespace-pre-line">
                    {form.address || "Company address"}
                  </p>
                  <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">
                    {[form.uen ? `UEN ${form.uen}` : null, form.phone || null, form.email || null]
                      .filter((line) => line)
                      .join("  ·  ") || "UEN, phone number, email address"}
                  </p>
                </div>
                {company.hasLogo ? (
                  <img
                    src={`/api/company/logo?at=${logoAt}`}
                    alt=""
                    className="max-h-[3rem] max-w-[7rem] object-contain"
                  />
                ) : null}
              </div>
              <div className="border-t border-dashed border-defaultborder dark:border-defaultborder/10 mt-4 pt-3">
                <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">
                  INVOICE INV-2026-0001  ·  9 October 2026
                </p>
              </div>
            </div>
            <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-3 mb-0">
              Every printed invoice and payment voucher uses these details in its heading.
            </p>
          </Box>
        </div>
      </div>
    </>
  );
}
